<template>
    <BaseWidget
        :id="id"
        :widget-name="widgetName"
        :has-content="!!content"
        :is-loading="isLoading"
        :is-expanded="isExpanded"
        :widget-type="widgetType"
        :widget-last-updated="widgetLastUpdated"
        v-on="$listeners"
    >
        <div slot="empty">
            <h2>{{ $t('dashboard.widgetType8.noAssignments') }}</h2>
        </div>
        <div v-show="!isLoading" class="widget-content widget-scrollable">
            <div class="widget-inner">
                <div class="wrapper-float">
                    <div v-if="isExpanded" class="inline shift-label">{{ $t('assignments.shift') }}:</div>
                    <div class="widget-selectbox-wrapper" @mousedown.stop.prevent>
                        <SelectBox
                            :value="selectedOption"
                            :options="duties"
                            style-class="inline"
                            :search="false"
                            @input="setSelectedOptions({ widgetId: id, selectedOption: $event })"
                        />
                    </div>
                </div>
                <div v-if="isNotEmptyAssignments" class="widget-table-wrapper">
                    <Component
                        :is="isPrintver ? 'div' : 'Simplebar'"
                        :class="isPrintver ? '' : 'with-border-track'"
                    >
                        <table class="assignments-widget-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>{{ $t('dashboard.widgetType8.callType') }}</th>
                                    <th>{{ $t('dashboard.widgetType8.locations') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-if="conf.assignments_type !== 2 && conf.show_resident_call"
                                    :class="{ 'direct-n-duty-divider': !conf.show_staff_call }"
                                >
                                    <td :class="{ 'one-call-category': !conf.show_staff_call }">
                                        <div>{{ $t('dashboard.widgetType8.direct') }}</div>
                                    </td>
                                    <td :class="'color-' + (!!conf.resident_color ? conf.resident_color : 2)">
                                        <i class="icon-normal"></i><span>{{ conf.resident_call_name }}</span>
                                    </td>
                                    <td>
                                        <div>
                                            {{ getDirectLocations('observer') ? directResident : '&mdash;' }}
                                        </div>
                                    </td>
                                </tr>
                                <tr
                                    v-if="conf.assignments_type !== 2 && conf.show_staff_call"
                                    class="direct-n-duty-divider"
                                >
                                    <td :class="{ 'one-call-category': !conf.show_resident_call }">
                                        <div>{{ $t('dashboard.widgetType8.direct') }}</div>
                                    </td>
                                    <td :class="'color-' + (!!conf.staff_color ? conf.staff_color : 1)">
                                        <i class="icon-assistance"></i><span>{{ conf.staff_call_name }}</span>
                                    </td>
                                    <td>
                                        <div>
                                            {{ getDirectLocations('assistance') ? directStaff : '&mdash;' }}
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="conf.assignments_type !== 1 && conf.show_resident_call">
                                    <td :class="{ 'one-call-category': !conf.show_staff_call }">
                                        <div>{{ $t('dashboard.widgetType8.duty') }}</div>
                                    </td>
                                    <td :class="'color-' + (!!conf.resident_color ? conf.resident_color : 2)">
                                        <i class="icon-normal"></i><span>{{ conf.resident_call_name }}</span>
                                    </td>
                                    <td>
                                        <div>
                                            {{ getDutyLocations('observer') ? dutyResident : '&mdash;' }}
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="conf.assignments_type !== 1 && conf.show_staff_call">
                                    <td :class="{ 'one-call-category': !conf.show_resident_call }">
                                        <div>{{ $t('dashboard.widgetType8.duty') }}</div>
                                    </td>
                                    <td :class="'color-' + (!!conf.staff_color ? conf.staff_color : 1)">
                                        <i class="icon-assistance"></i><span>{{ conf.staff_call_name }}</span>
                                    </td>
                                    <td>
                                        <div>
                                            {{ getDutyLocations('assistance') ? dutyStaff : '&mdash;' }}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Component>
                </div>
                <div v-else class="no-widget">
                    <div class="no-widget-data">
                        <h2>{{ $t('dashboard.widgetType8.noAssignments') }}</h2>
                    </div>
                </div>
            </div>
        </div>
    </BaseWidget>
</template>

<script type="text/babel">
import { mapMutations, mapGetters } from 'vuex';
import widgetInterface from './common/widgetInterface';
import BaseWidget from '../BaseWidget';
import SelectBox from '../../common/SelectBox.vue';
import some from 'lodash-es/some';
import { convertDuties, DEFAULT_ASSIGNMENTS_CONFIGURATION } from '../../assignments/assignmentsUtils';
import Simplebar from 'simplebar-vue';

export default {
    components: {
        BaseWidget,
        SelectBox,
        Simplebar,
    },

    props: widgetInterface,

    data() {
        return {
            defaultShift: '1',
        };
    },

    computed: {
        ...mapGetters({
            getSelectedOptionById: 'dashboard/getSelectedOptionById',
        }),

        /**
         * Returns widget selected option
         * @returns {string}
         */
        selectedOption() {
            let defShift = this.defaultShift;
            if (this.content && 'activeShift' in this.content) {
                defShift = this.content.activeShift;
            }
            return this.getSelectedOptionById(this.id) || defShift;
        },

        /**
         * Returns widget name
         * @returns {string}
         */
        widgetName() {
            return this.configuration.name || this.$t('dashboard.widgetType8.name');
        },

        /**
         * Gets assignments configuration object from widget content
         * or provides default configuration
         * @returns {object}
         */
        conf() {
            return (this.content || {}).assignments_configuration || DEFAULT_ASSIGNMENTS_CONFIGURATION;
        },

        /**
         * Returns list of options for duty selector
         * @returns {object[]}
         */
        duties() {
            if (
                !this.content ||
                !this.content.duty ||
                typeof this.content.duty !== 'object' ||
                Object.keys(this.content.duty).length === 0
            ) {
                return [];
            }
            const duty = this.content.duty;
            let shifts = Object.keys(duty);
            let duties = shifts.map(shift => ({
                state: shift,
                duty_name: duty[shift].duty_name,
                start_time: duty[shift].start_time,
            }));

            return convertDuties(this.$t.bind(this), duties, this.content.duty_has_selector);
        },

        /**
         * Checks whether or not user is assigned to any location
         * @returns {boolean}
         */
        isNotEmptyAssignments() {
            const content = this.content;
            if (!content) {
                return false;
            }

            const hasDirect = type => !!content.direct[type] && content.direct[type].length > 0;
            const hasDuty = type => some(content.duty, duty => !!duty[type] && duty[type].length > 0);

            return (
                (!!content.direct && (hasDirect('observer') || hasDirect('assistance'))) ||
                (!!content.duty && (hasDuty('observer') || hasDuty('assistance')))
            );
        },

        /**
         * Returns location string for direct assignments, normal call
         * @returns {string}
         */
        directResident() {
            return this.getDirectLocations('observer');
        },

        /**
         * Returns location string for direct assignments, assistance call
         * @returns {string}
         */
        directStaff() {
            return this.getDirectLocations('assistance');
        },

        /**
         * Returns location string for duty assignments, current shift, normal call
         * @returns {string}
         */
        dutyResident() {
            return this.getDutyLocations('observer');
        },

        /**
         * Returns location string for duty assignments, current shift, assistance call
         * @returns {string}
         */
        dutyStaff() {
            return this.getDutyLocations('assistance');
        },
    },

    watch: {
        duties(val) {
            if (!!val && val.length > 0 && val instanceof Array) {
                let shifts = val.map(d => d.value);
                if (!shifts.includes(this.selectedOption) && shifts.length > 0) {
                    // select first available shift if there were no valid shift selected before
                    this.setSelectedOptions({ widgetId: this.id, selectedOption: shifts[0] });
                }
            }
        },
    },

    methods: {
        ...mapMutations({
            setSelectedOptions: 'dashboard/SET_SELECTED_WIDGET_DATA',
        }),

        /**
         * Gets locations string for direct assignments
         * @param {string} type may be "observer" or "assistance"
         * @returns {string}
         */
        getDirectLocations(type) {
            const content = this.content;

            return !!content && !!content.direct && !!content.direct[type]
                ? content.direct[type].join(', ')
                : '';
        },

        /**
         * Gets locations string for duty assignments for current shift
         * @param {string} type may be "observer" or "assistance"
         * @returns {string}
         */
        getDutyLocations(type) {
            const content = this.content;
            const shift = this.selectedOption;

            return !!content && !!content.duty && shift !== null && !!content.duty[shift]
                ? (content.duty[shift][type] || []).join(', ')
                : '';
        },
    },
};
</script>
